import { useEffect, useState } from 'react';
import { Star, Plus, X, ExternalLink } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import SEO from '../components/SEO';
import { LOCAL_BUSINESS_SCHEMA } from '../lib/schema';
import { fetchReviews, submitReview } from '../lib/api';
import { useLang } from '../context/LanguageContext';

const Reviews = () => {
  const { c, ui } = useLang();
  const t = ui.reviews;
  const [apiReviews, setApiReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', service: '', rating: 5, text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchReviews().then(setApiReviews).catch(() => setApiReviews([]));
  }, []);

  const reviews = [
    ...c.googleReviews.items.map(r => ({ ...r, source: 'google' })),
    ...apiReviews.map(r => ({ ...r, vehicle: r.service, source: 'api' })),
  ];

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitReview(form);
      setSubmitted(true);
      setForm({ name: '', email: '', service: '', rating: 5, text: '' });
      setTimeout(() => { setOpen(false); setSubmitted(false); }, 3000);
    } catch {
      alert(t.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0a0a0b] text-white pt-32 pb-24">
      <SEO
        path="/reviews"
        title={`Reviews — ${c.googleReviews.rating} ★ op Google (${c.googleReviews.count} reviews)`}
        description={`Klanten beoordelen Shiny-Cleaning met een ${c.googleReviews.rating}-sterren gemiddelde op Google. Lees ervaringen van BMW-, Audi-, Porsche- en Mercedes-eigenaren.`}
        keywords="Shiny-Cleaning reviews, autodetailing ervaringen Deventer, Google reviews"
        jsonLd={[LOCAL_BUSINESS_SCHEMA]}
      />
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionLabel>{t.label}</SectionLabel>
            <h1 className="mt-5 text-5xl md:text-7xl font-medium tracking-tight leading-[1.02]">{t.title}</h1>
          </div>
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#2f6bff] hover:bg-[#1f56e8] text-white text-sm transition-colors">
            <Plus className="w-4 h-4" /> {t.write}
          </button>
        </div>

        <a
          href={c.googleReviews.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-12 flex items-center gap-6 pb-10 border-b border-white/10 group hover:opacity-90 transition-opacity"
        >
          <span className="w-14 h-14 rounded-full bg-white flex items-center justify-center flex-shrink-0">
            <svg viewBox="0 0 48 48" width="28" height="28" aria-hidden="true">
              <path fill="#4285F4" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
              <path fill="#34A853" d="M6.3 14.7l6.6 4.8C14.7 15.8 19 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.1 4 9.3 8.3 6.3 14.7z" />
              <path fill="#FBBC05" d="M24 44c5.2 0 9.8-2 13.3-5.3l-6.1-5.1c-2 1.4-4.5 2.2-7.2 2.2-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.1 39.5 16 44 24 44z" />
              <path fill="#EA4335" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.1 5.1C41.5 35.7 44 30.3 44 24c0-1.2-.1-2.4-.4-3.5z" />
            </svg>
          </span>
          <div>
            <div className="flex items-center gap-3">
              <span className="text-5xl font-medium">{c.googleReviews.rating}</span>
              <div className="flex gap-0.5 text-[#f5b301]">
                {Array.from({ length: 5 }).map((_, k) => <Star key={k} className="w-5 h-5 fill-current" />)}
              </div>
            </div>
            <div className="text-white/60 text-sm mt-1 flex items-center gap-1.5">
              {c.googleReviews.count} {t.googleReviews}
              <ExternalLink className="w-3 h-3 opacity-70 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>
        </a>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <div key={r.id || i} className="p-7 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <div className="flex gap-0.5 mb-4 text-[#f5b301]">
                {Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}
              </div>
              <p className="text-white/85 leading-relaxed mb-6">&ldquo;{r.text}&rdquo;</p>
              <div className="flex justify-between items-center text-sm pt-4 border-t border-white/5">
                <span className="text-white font-medium">{r.name}</span>
                <span className="text-white/40 text-xs">{r.vehicle || r.service}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="relative w-full max-w-lg bg-[#111114] border border-white/10 rounded-2xl p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
            <h3 className="text-2xl font-medium mb-2">{t.modalTitle}</h3>
            <p className="text-white/50 text-sm mb-6">{t.modalSubtitle}</p>
            {submitted ? (
              <div className="py-12 text-center">
                <div className="text-[#2f6bff] text-xl">{t.thanksTitle}</div>
                <p className="text-white/60 mt-2 text-sm">{t.thanksText}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <input required placeholder={t.namePh} value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-[#2f6bff]" />
                <input required type="email" placeholder={t.emailPh} value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-[#2f6bff]" />
                <select required value={form.service} onChange={e => setForm({...form, service: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-[#2f6bff]">
                  <option value="" className="bg-[#111114]">{t.servicePh}</option>
                  {c.services.map(s => <option key={s.slug} value={s.title} className="bg-[#111114]">{s.title}</option>)}
                </select>
                <div className="flex items-center gap-2">
                  <span className="text-white/60 text-sm">{t.ratingLabel}</span>
                  {[1,2,3,4,5].map(n => (
                    <button type="button" key={n} onClick={() => setForm({...form, rating: n})}>
                      <Star className={`w-6 h-6 ${n <= form.rating ? 'text-[#f5b301] fill-current' : 'text-white/20'}`} />
                    </button>
                  ))}
                </div>
                <textarea required rows={4} placeholder={t.experiencePh} value={form.text} onChange={e => setForm({...form, text: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white text-sm focus:outline-none focus:border-[#2f6bff] resize-none" />
                <button type="submit" disabled={submitting} className="w-full px-6 py-3 rounded-md bg-[#2f6bff] hover:bg-[#1f56e8] disabled:opacity-60 text-white text-sm font-medium transition-colors">
                  {submitting ? t.submitting : t.submit}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
